import { PerformanceDatapoint } from 'owa-analytics';
import {
    lazyAddRecipientsIntoLink,
    lazySetGetPermissionInfoFailure,
    lazyUpdateRecipientPermissionInfos,
    RecipientPermissionResult,
} from 'owa-link-data';
import { format } from 'owa-localize';
import { RecipientPermissionInfo } from 'owa-sharing-data';
import { trace } from 'owa-trace';
import { checkRecipientPermissionService } from '../services/checkRecipientPermission';
import { finishCheckPerm, startCheckPerm } from '../actions/permissionCheckerInternalActions';
import { shouldCheckPermForLink } from './shouldCheckPermForLink';

const RECIPIENTS_MAX_NUM_PER_REQUEST: number = 30;
const MAX_NUMBER_OF_RECIPIENTS_TO_LOG: number = 23;

export async function checkRecipientsPermission(
    newRecipientPermissionInfos: RecipientPermissionInfo[],
    linkId: string,
    linkUrl: string,
    composeId: string
): Promise<boolean> {
    if (newRecipientPermissionInfos.length > 0) {
        trace.info(
            format(
                'RecipientPermission: LinkId:{0}, add {1} recipients',
                linkId,
                newRecipientPermissionInfos.length
            )
        );

        let recipients: RecipientPermissionInfo[] = null;
        const addRecipientsIntoLink = await lazyAddRecipientsIntoLink.import();
        if (!shouldCheckPermForLink(linkId)) {
            return false;
        }

        addRecipientsIntoLink(linkId, newRecipientPermissionInfos);

        // startCheckPerm and finishCheckPerm are paired to track the pending check perm
        // We should be very careful when moving them to guarantee that startCheckPerm is followed with finishCheckPerm when the operation finishes.
        startCheckPerm(composeId);
        let datapoint = new PerformanceDatapoint('LinkCheckPerms');
        datapoint.addCustomData({ RecipientCount: newRecipientPermissionInfos.length });
        try {
            for (
                let i: number = 0;
                i < newRecipientPermissionInfos.length / RECIPIENTS_MAX_NUM_PER_REQUEST;
                i++
            ) {
                recipients = newRecipientPermissionInfos.slice(
                    i * RECIPIENTS_MAX_NUM_PER_REQUEST,
                    (i + 1) * RECIPIENTS_MAX_NUM_PER_REQUEST
                );

                if (!shouldCheckPermForLink(linkId)) {
                    datapoint.addCustomData({ permissionChangedSoCheckPermStoped: true });
                    datapoint.end();
                    datapoint = null;
                    return false;
                }
                const recipientPermissionResults: RecipientPermissionResult[] = await checkRecipientPermissionService(
                    linkUrl,
                    recipients
                );

                const countOfRecipientsWhoCannotAccess: number = recipientPermissionResults.filter(
                    x => !x.hasAccess
                ).length;
                const updateRecipientPermissionInfos = await lazyUpdateRecipientPermissionInfos.import();
                updateRecipientPermissionInfos(linkId, recipientPermissionResults);
                datapoint.addCustomData({
                    countOfRecipientsWhoCannotAccess: Math.min(
                        countOfRecipientsWhoCannotAccess,
                        MAX_NUMBER_OF_RECIPIENTS_TO_LOG
                    ),
                });
                trace.info(
                    format(
                        'RecipientPermission: checkAccess for {0} recipients succeeded',
                        recipientPermissionResults.length
                    )
                );
            }
        } catch (error) {
            handleFailure(linkId, recipients || newRecipientPermissionInfos, error.message);
            datapoint.endWithError(error.message);
            datapoint = null;
            return false;
        } finally {
            datapoint?.end();
            finishCheckPerm(composeId);
        }
    }
    return true;
}

export async function handleFailure(
    linkId: string,
    recipientPermissionInfos: RecipientPermissionInfo[],
    errorMessage: string
) {
    trace.info(
        format(
            'RecipientPermission: checkAccess for {0} recipients failed',
            recipientPermissionInfos.length
        )
    );

    const setGetPermissionInfoFailure = await lazySetGetPermissionInfoFailure.import();
    setGetPermissionInfoFailure(linkId, recipientPermissionInfos);
}
